/*
 *  -----------------------------------------------------------------------
 *    <copyright file="central-monitoring-report.tsx" company="Triumph Research Intelligence">
 *        Copyright (C) 2019 by Triumph Research Intelligence. All rights reserved.
 *    </copyright>
 *   -----------------------------------------------------------------------
 *
 */

import React, { useState, useEffect } from 'react';
import { useLazyQuery, useQuery } from '@apollo/client/react';

import DeleteSignatory from '@components/reports/delete-signatory';
import { ITemplate } from '@components/reports/i-template';
import { Permissions } from 'contexts/user-context';
import { useLocalState, useGridCells, useTranslation, useBoolean, useAuthorization } from 'hooks';
import {
    ReportSignatoryFields,
    ReportFields,
    ReportFields_Report_dataSet_DataSet_reports_DataSetReport as ReportFields_dataSet_reports,
} from 'queries/generated/fragments.generated';
import { GET_REPORT_SIGNATORIES, GET_REPORTS, GET_PDF_REPORT } from 'queries/reports';
import { GET_STUDY } from 'queries/studies';
import { AuditServiceType, ReportStatus } from 'types/graphql-global-types';
import Constants from 'utils/constants';
import { downloadBase64File } from 'utils/file-service';
import NotificationService from 'utils/notification';
import GenerateReport from 'views/components/central-monitoring/reports/generate-report';
import PreviewReport from 'views/components/central-monitoring/reports/preview-report';
import DialogInformationBox from 'views/components/core/dialog-information-box/dialog-information-box';
import Loading from 'views/components/core/loading';
import PageTitle from 'views/components/core/page-title/page-title';
import SelectWithCategories from 'views/components/core/select-with-categories';
import Error from 'views/components/error/error';
import EditReport from 'views/components/reports/edit-report';
import EditSignatory from 'views/components/reports/edit-signatory';
import PDFSignatory from 'views/components/reports/pdf-report-wrapper/signatory';
import PDFVersions from 'views/components/reports/pdf-report-wrapper/versions';
import ReportExistsModal from 'views/components/reports/report-exists-modal';

import { addPrefixedRecordIdsToTemplates } from '@utils/prefixes-helper';
import { AUDIT_RECORD_ID_PREFIXES } from '@queries/audit';
import { auditRecordIdPrefixes } from '@queries/generated/audit.generated';

const CentralMonitoringReport = () => {
    const { state } = useLocalState();
    const { t } = useTranslation();
    const { DateCell } = useGridCells();
    const [selectedItem, setSelectedItem] = useState('CentralMonitoringReview');

    const user = useAuthorization();
    const canView = !!user?.canRead?.({
        module: Constants.Modules.CM,
        element: Constants.DataElements.CentralMonitoring,
        permission: Permissions.Read,
    });
    const canEdit = !!user?.canWrite?.({
        module: Constants.Modules.CM,
        element: Constants.DataElements.CentralMonitoring,
        permission: Permissions.ReadWrite,
    });

    const PDFTemplates = [
        { text: t('ra:selectValue'), id: 'none' },
        { text: t('cm:CMReview'), id: 'CentralMonitoringReview', description: 'CMReviewDescription' },
    ];

    const [selectedItemFull, setSelectedItemFull] = useState<ITemplate | null>(PDFTemplates[1]);
    const [selectedSignatory, setSelectedSignatory] = useState<ReportSignatoryFields | null>(null);
    const [selectedReport, setSelectedReport] = useState<ReportFields | null>(null);
    const [stateForGenerating, setStateForGenerating] = useState(null);

    const [getSignatories, { data: dataSignatories, loading: loadingSignatoriesData, error: errorSignatories }] =
        useLazyQuery(GET_REPORT_SIGNATORIES);
    const { data: prefixesData, loading: loadingPrefixesData } = useQuery<auditRecordIdPrefixes>(
        AUDIT_RECORD_ID_PREFIXES,
        { fetchPolicy: 'cache-first' },
    );

    const loadingSignatories = loadingSignatoriesData || loadingPrefixesData;

    const [getReports, { data: dataReports, loading: loadingReports, error: errorReports }] = useLazyQuery(GET_REPORTS);
    const [getReport] = useLazyQuery(GET_PDF_REPORT);
    const { data: actualStudy, loading: loadingStudy } = useQuery(GET_STUDY, {
        variables: {
            id: state?.selectedStudy?.id,
        },
    });

    const showNewSignatory = useBoolean(false);
    const showDeleteSignatory = useBoolean(false);
    const showReportExists = useBoolean(false);
    const showEditReport = useBoolean(false);
    const showGenerateReport = useBoolean(false);
    const showPreviwReport = useBoolean(false);

    const study = state.selectedStudy;

    const makeRequestsForData = (value: string) => {
        getSignatories({
            variables: {
                studyId: study.id,
                template: value,
            },
        });
        getReports({
            variables: {
                studyId: study.id,
                template: value,
            },
        });
    };

    useEffect(() => {
        if (study && selectedItem) makeRequestsForData(selectedItem);
    }, [study]);

    const signatories = addPrefixedRecordIdsToTemplates<ReportSignatoryFields>(
        dataSignatories?.reportSignatories ?? [],
        AuditServiceType.REPORTING,
        prefixesData?.auditRecordIdPrefixes,
    );
    const reports = dataReports?.reports.map((report: ReportFields) => ({
        ...report,
        siteInclusion: report.dataSet?.reports?.find((r: ReportFields_dataSet_reports) => r.reportId === report.id)
            ?.includeAllSites
            ? t('reports:allSites')
            : t('reports:reviewedSites'),
        datasetDate: report.dataSet?.dataCutDate,
    }));

    const showGenerateModal = reports
        ? reports?.findIndex((item: ReportFields) => item.status === ReportStatus.PREVIEW) < 0
        : true;

    if (!canView) return <h1>{t('unauthorised')}</h1>;

    if (loadingStudy) return <Loading float />;

    return (
        <>
            <EditReport
                open={showEditReport.value}
                template={selectedItem}
                studyId={study?.id}
                report={selectedReport}
                canEdit={canEdit}
                onClose={(action?: string) => {
                    if (action === 'error') NotificationService.showNotification(t('errorMessage'));
                    else {
                        if (action === 'add')
                            NotificationService.showNotification(t('reports:reportSuccessfulCreated'));
                        else if (action === 'edit')
                            NotificationService.showNotification(
                                t('reports:reportSuccessfulUpdated', {
                                    id: selectedReport?.recordId ?? selectedReport?.id,
                                }),
                            );
                        setSelectedReport(null);
                        showEditReport.setFalse();
                    }
                }}
            />
            <PreviewReport
                open={showPreviwReport.value}
                template={selectedItem}
                study={study}
                stateForGenerate={stateForGenerating}
                onClose={(action?: string) => {
                    if (action === 'error') NotificationService.showNotification(t('errorMessage'));
                    else {
                        if (action === 'generate')
                            NotificationService.showNotification(t('reports:reportSuccessfulGenerated'));
                        showPreviwReport.setFalse();
                    }
                }}
                reports={reports}
            />
            <GenerateReport
                open={showGenerateReport.value}
                signatories={signatories}
                studyId={study?.id}
                onClose={(action?: string, value?: any) => {
                    if (action === 'error') NotificationService.showNotification(t('errorMessage'));
                    else {
                        if (action === 'open-preview') {
                            setStateForGenerating(value);
                            showPreviwReport.setTrue();
                        }
                        showGenerateReport.setFalse();
                    }
                }}
            />
            <ReportExistsModal
                open={showReportExists.value}
                onClose={() => {
                    showReportExists.setFalse();
                }}
            />
            <EditSignatory
                open={showNewSignatory.value}
                signatory={selectedSignatory}
                onClose={(action?: string) => {
                    if (action === 'error') NotificationService.showNotification(t('errorMessage'));
                    else {
                        if (action === 'add')
                            NotificationService.showNotification(t('reports:signatorySuccessfulCreated'));
                        else if (action === 'edit')
                            NotificationService.showNotification(
                                t('reports:signatorySuccessfulUpdated', {
                                    id: selectedSignatory?.recordId ?? selectedSignatory?.id,
                                }),
                            );
                        setSelectedSignatory(null);
                        showNewSignatory.setFalse();
                    }
                }}
                template={selectedItem}
                studyId={study?.id}
                canEdit={canEdit}
            />
            <DeleteSignatory
                open={showDeleteSignatory.value}
                template={selectedItem}
                studyId={study?.id}
                signatoryId={selectedSignatory?.id}
                onClose={(action?: string) => {
                    if (action === 'error') NotificationService.showNotification(t('errorMessage'));
                    else {
                        if (action === 'success')
                            NotificationService.showNotification(
                                t('reports:signatorySuccessfulDeleted', {
                                    id: selectedSignatory?.recordId ?? selectedSignatory?.id,
                                }),
                            );
                        setSelectedSignatory(null);
                        showDeleteSignatory.setFalse();
                    }
                }}
                canEdit={canEdit}
            />
            <PageTitle text={t('reports:pdfReportDetails')} />
            <br />
            <SelectWithCategories
                label={t('reports:pdfReport')}
                onChange={() => (event: any) => {
                    setSelectedItem(event.target.value);
                    setSelectedItemFull(PDFTemplates.find((item: any) => item.id === event.target.value));
                    makeRequestsForData(event.target.value);
                }}
                dataArray={PDFTemplates}
                labelField="pdf"
                fieldSelect="pdf"
                customClassName="select-pdf-report"
                showWarning={false}
                currentState={selectedItem}
                customId="select-pdf-report"
                filterable={false}
                searchable
            />
            {selectedItem && selectedItem !== 'none' ? (
                <div className="info-box-report">
                    <DialogInformationBox topText={t(`cm:${selectedItemFull?.description}`)} />
                </div>
            ) : null}
            {(!selectedItem || selectedItem === 'none') && (
                <p className="no-report-selected">{t('reports:selectReportToViewDetails')}</p>
            )}
            {selectedItem && selectedItem !== 'none' ? (
                <>
                    {!loadingReports && !errorReports ? (
                        <PDFVersions
                            data={reports}
                            canEdit={canEdit}
                            additionalColumns={[
                                {
                                    field: 'siteInclusion',
                                    id: 'versions-site-inclusion',
                                    label: t('cm:siteInclusion'),
                                    minWidth: 230,
                                },
                                {
                                    field: 'datasetDate',
                                    id: 'versions-dataset-date',
                                    label: t('cm:dataSetDate'),
                                    filter: 'date',
                                    minWidth: 230,
                                    cells: { data: DateCell },
                                },
                            ]}
                            spliceIndex={3}
                            openNew={(value: boolean) => {
                                if (showGenerateModal) showGenerateReport.setValue(value);
                                else showReportExists.setValue(value);
                            }}
                            actions={(item: any) => [
                                {
                                    label: 'reports:editReport',
                                    icon: ['fal', 'edit'],
                                    onClick: () => {
                                        setSelectedReport(item);
                                        showEditReport.setTrue();
                                    },
                                    enabled: canEdit,
                                },
                                {
                                    label: 'reports:downloadReport',
                                    enabled: true,
                                    icon: ['fal', 'download'],
                                    onClick: async () => {
                                        const result = await getReport({
                                            variables: {
                                                id: item.id,
                                            },
                                        });

                                        const data = result?.data as any;

                                        if (data?.pdfReport) {
                                            downloadBase64File(`${selectedItem}.pdf`, data.pdfReport);
                                        }
                                    },
                                },
                            ]}
                        />
                    ) : null}
                    {loadingSignatories ? <Loading /> : null}
                    {errorSignatories ? <Error error={errorSignatories} /> : null}
                    {!loadingSignatories && !errorSignatories ? (
                        <PDFSignatory
                            data={signatories}
                            openNew={(value: boolean | undefined) => {
                                showNewSignatory.setValue(value);
                            }}
                            canEdit={canEdit}
                            actions={(item: ReportSignatoryFields) => [
                                {
                                    label: 'reports:editSignatory',
                                    icon: ['fal', 'edit'],
                                    onClick: () => {
                                        setSelectedSignatory(item);
                                        showNewSignatory.setTrue();
                                    },
                                    enabled: canEdit,
                                },
                                {
                                    label: 'reports:deleteSignatory',
                                    enabled: canEdit,
                                    icon: ['fal', 'trash'],
                                    onClick: () => {
                                        setSelectedSignatory(item);
                                        showDeleteSignatory.setTrue();
                                    },
                                },
                            ]}
                        />
                    ) : null}
                </>
            ) : null}
        </>
    );
};

export default CentralMonitoringReport;
